import { StructuredOutput } from '@/types'

export type RequestOptions = RequestInit & {
    timeout?: number
    retries?: number
}

export const initOutput = <T>(): StructuredOutput<T> => ({
    success: false,
    error: '',
    data: undefined,
})

export const fetchWithTimeout = async (url: string, options: RequestOptions = {}): Promise<Response> => {
    const { timeout = 15000, retries = 0, ...fetchOptions } = options
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
        const response = await fetch(url, {
            ...fetchOptions,
            signal: controller.signal,
        })

        if (!response.ok) {
            throw new Error(`HTTP error status: ${response.status}`)
        }

        return response
    } catch (error) {
        if (retries > 0) {
            return fetchWithTimeout(url, { ...options, retries: retries - 1 })
        }
        throw error
    } finally {
        clearTimeout(timeoutId)
    }
}

export const defaultHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
} as const

export const createRequest = async <T>(url: string, options: RequestOptions = {}): Promise<StructuredOutput<T>> => {
    const output = initOutput<T>()

    try {
        const response = await fetchWithTimeout(url, {
            ...options,
            headers: { ...defaultHeaders, ...options.headers },
        })
        const data = await response.json()
        return { ...output, success: true, data }
    } catch (error) {
        return {
            ...output,
            error: error instanceof Error ? error.message : 'An unknown error occurred',
        }
    }
}

export async function safePromise<T>(promise: Promise<T>): Promise<{
    data: T | null
    error: Error | null
}> {
    try {
        const result = await promise
        return {
            data: result,
            error: null,
        }
    } catch (error) {
        return {
            data: null,
            error: error instanceof Error ? error : new Error(String(error)),
        }
    }
}

export async function unwrapSafePromise<T, E = Error>(
    promise: Promise<{
        data: T | null
        error: E
    }>,
): Promise<T> {
    const { error, data } = await promise
    if (error) {
        throw error
    }

    if (data === null) {
        throw new Error('Data is null but no error was provided')
    }

    return data
}

// type guard for error with status
export function hasStatus(err: unknown): err is { status: number } {
    return typeof err === 'object' && err !== null && 'status' in err && typeof (err as { status: unknown }).status === 'number'
}
// type guard for error with response.status
export function hasResponseStatus(err: unknown): err is { response: { status: number } } {
    return (
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response: { status: unknown } }).response?.status === 'number'
    )
}
